<script setup lang="ts">
import { computed } from 'vue';

export interface Props {
  previousJobId?: number;
  nextJobId?: number;
  currentJobId?: number;
  position?: string;
  canNavigate?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  previousJobId: undefined,
  nextJobId: undefined,
  currentJobId: undefined,
  position: '',
  canNavigate: false,
  disabled: false,
});

const emit = defineEmits<{
  previousClick: [event: Event];
  nextClick: [event: Event];
}>();

const isSingleJob = computed(() => {
  return !props.canNavigate && typeof props.currentJobId === 'number';
});

function handlePreviousClick(event: Event): void {
  if (props.disabled || !props.canNavigate) return;
  emit('previousClick', event);
}

function handleNextClick(event: Event): void {
  if (props.disabled || !props.canNavigate) return;
  emit('nextClick', event);
}
</script>

<template>
  <footer
    :class="$style.jobCarouselNavigation"
    data-testid="jobCarouselNavigation"
  >
    <span v-if="isSingleJob" :class="$style.singleJobLabel">
      Job #{{ props.currentJobId }}
    </span>

    <div
      v-if="!isSingleJob && props.canNavigate"
      :class="[$style.navButton, { [$style.disabled]: props.disabled }]"
      @click="handlePreviousClick"
    >
      <v-icon size="20">mdi-chevron-left</v-icon>
      <span :class="$style.navJobId">Job #{{ props.previousJobId }}</span>
    </div>

    <span v-if="!isSingleJob && props.position" :class="$style.position">
      {{ props.position }}
    </span>

    <div
      v-if="!isSingleJob && props.canNavigate"
      :class="[$style.navButton, { [$style.disabled]: props.disabled }]"
      @click="handleNextClick"
    >
      <span :class="$style.navJobId">Job #{{ props.nextJobId }}</span>
      <v-icon size="20">mdi-chevron-right</v-icon>
    </div>
  </footer>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobCarouselNavigation {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  height: 56px;
  background-color: colors.$color-white;
  border-top: 1px solid colors.$color-gray-200;
  border-radius: 0 0 8px 8px;
  padding: 0 16px;
}

.navButton {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: colors.$color-text-primary;

  &:hover:not(.disabled) {
    background-color: colors.$color-gray-50;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.navJobId {
  font-size: 14px;
  font-weight: 600;
  color: colors.$color-text-primary;
  white-space: nowrap;
}

.position {
  font-size: 14px;
  color: colors.$color-text-secondary;
}

.singleJobLabel {
  font-size: 14px;
  font-weight: 400;
  color: colors.$color-text-primary;
}
</style>
